// RatingFormUsers.js - Enhanced Component for Mentor Comments
import React, { useEffect, useState, useCallback } from 'react';
import { 
    CircularProgress, 
    Button, 
    Box, 
    Typography,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Grid,
    Paper,
    Avatar,
    Chip,
    Stack,
    useTheme,
    alpha,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { 
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    Email as EmailIcon,
    Comment as CommentIcon,
    ExpandMore as ExpandMoreIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import RatingPopup from '../Self/RatingPopup';
import { getAllUserRatingResults, getUsers } from '../../Queries';

const RatingFormUsers = ({ questions, status, form, refreshForms }) => {
    const theme = useTheme();
    const [submittedUsers, setSubmittedUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [openPopup, setOpenPopup] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [debugInfo, setDebugInfo] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    const getRatingFormUsers = useCallback(async (forceRefresh = false) => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Fetch data with error handling and validation
            // Add timeout to prevent hanging requests
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000)
            );
            
            const [usersResponse, ratingsResponse] = await Promise.race([
                Promise.all([
                    getUsers(forceRefresh),
                    getAllUserRatingResults(forceRefresh)
                ]),
                timeoutPromise
            ]);
            // Validate and normalize users data
            let validUsers = [];
            if (usersResponse === null || usersResponse === undefined) {
                console.warn('Users response is null or undefined');
                validUsers = [];
            } else if (Array.isArray(usersResponse)) {
                validUsers = usersResponse;
            } else if (usersResponse && Array.isArray(usersResponse.users)) {
                validUsers = usersResponse.users;
            } else if (usersResponse && typeof usersResponse === 'object') {
                // Handle case where users might be an object with user data
                validUsers = Object.values(usersResponse).filter(item => 
                    item && typeof item === 'object' && (item.email || item.id)
                );
            }
            
            // Don't throw error if no users - just show empty state
            if (validUsers.length === 0) {
                console.warn('No valid users data received - this might be expected if no users exist');
            }
            
            // Validate and normalize ratings data
            let validRatings = [];
            if (ratingsResponse === null || ratingsResponse === undefined) {
                console.warn('Ratings response is null or undefined');
                validRatings = [];
            } else if (Array.isArray(ratingsResponse)) {
                validRatings = ratingsResponse;
            } else if (ratingsResponse && Array.isArray(ratingsResponse.ratingResults)) {
                validRatings = ratingsResponse.ratingResults;
            } else if (ratingsResponse && Array.isArray(ratingsResponse.results)) {
                validRatings = ratingsResponse.results;
            } else if (ratingsResponse && typeof ratingsResponse === 'object') {
                // Handle case where ratings might be nested differently
                const possibleArrays = Object.values(ratingsResponse).filter(Array.isArray);
                if (possibleArrays.length > 0) {
                    validRatings = possibleArrays[0];
                } else {
                    console.warn('No valid array found in ratings response object');
                }
            }
            
            // More robust form ID comparison
            const formId = form.form_id?.toString();
            const formRatings = validRatings.filter(rating => {
                if (!rating) return false;
                
                // Try multiple possible field names for parent_id
                const ratingParentId = rating.parent_id?.S || 
                                     rating.parent_id || 
                                     rating.form_id?.S || 
                                     rating.form_id ||
                                     rating.formId?.S ||
                                     rating.formId;
                
                return ratingParentId?.toString() === formId;
            });
            
            // Better user matching logic with deduplication
            const userRatingsMap = new Map();
            
            // First, group ratings by user
            formRatings.forEach(rating => {
                if (!rating) return;
                
                // Try multiple possible field names for user_id
                const userId = rating.user_id?.S || 
                              rating.user_id || 
                              rating.email?.S || 
                              rating.email ||
                              rating.userEmail?.S ||
                              rating.userEmail;
                
                if (!userId) {
                    console.warn('No user ID found in rating:', rating);
                    return;
                }
                
                // Group ratings by user
                if (!userRatingsMap.has(userId)) {
                    userRatingsMap.set(userId, []);
                }
                userRatingsMap.get(userId).push(rating);
            });
            
            const submittedUsersData = [];
            
            // Process each user's ratings and select the best one
            userRatingsMap.forEach((userRatings, userId) => {
                // Find matching user with multiple strategies
                const user = validUsers.find(u => {
                    if (!u) return false;
                    
                    const userEmail = u.email || u.user_email || u.userEmail;
                    const userIdField = u.id || u.user_id || u.userId;
                    
                    return userEmail === userId || 
                           userIdField?.toString() === userId?.toString() ||
                           u.username === userId;
                });
                
                // Select the best rating for this user
                let bestRating = userRatings[0];
                if (userRatings.length > 1) {
                    // Priority system:
                    // 1. Most recent submission
                    // 2. Has mentor comment
                    // 3. Complete rating data
                    bestRating = userRatings.sort((a, b) => {
                        // Sort by creation date (newest first)
                        const aDate = new Date(a.created_at?.S || a.created_at || a.createdAt?.S || a.createdAt || 0);
                        const bDate = new Date(b.created_at?.S || b.created_at || b.createdAt?.S || b.createdAt || 0);
                        
                        if (aDate !== bDate) {
                            return bDate - aDate; // Newer first
                        }
                        
                        // Prefer entries with mentor comments
                        const aMentorComment = a.mentor_comment?.S || a.mentor_comment || a.mentorComment?.S || a.mentorComment;
                        const bMentorComment = b.mentor_comment?.S || b.mentor_comment || b.mentorComment?.S || b.mentorComment;
                        
                        if (!!aMentorComment !== !!bMentorComment) {
                            return !!bMentorComment - !!aMentorComment; // With comment first
                        }
                        
                        return 0;
                    })[0];
                }
                
                if (!user) {
                    console.warn('No matching user found for rating:', { userId, rating: bestRating });
                    // Still add the entry with minimal info
                    submittedUsersData.push({
                        id: userId,
                        name: userId.includes('@') ? userId.split('@')[0] : userId,
                        email: userId,
                        ratingId: bestRating.id?.S || bestRating.id || `${userId}_${Date.now()}`,
                        ratingData: bestRating,
                        submittedAt: bestRating.created_at?.S || bestRating.created_at || bestRating.createdAt?.S || bestRating.createdAt,
                        mentorComment: bestRating.mentor_comment?.S || bestRating.mentor_comment || bestRating.mentorComment?.S || bestRating.mentorComment || '',
                        hasRating: true,
                        isUnknownUser: true,
                        duplicateCount: userRatings.length
                    });
                    return;
                }
                
                submittedUsersData.push({
                    id: user.id || user.user_id || userId,
                    name: user.name || user.full_name || user.fullName || user.first_name + ' ' + user.last_name || 'Unknown User',
                    email: user.email || user.user_email || userId,
                    ratingId: bestRating.id?.S || bestRating.id || `${userId}_${Date.now()}`,
                    ratingData: bestRating,
                    submittedAt: bestRating.created_at?.S || bestRating.created_at || bestRating.createdAt?.S || bestRating.createdAt,
                    mentorComment: bestRating.mentor_comment?.S || bestRating.mentor_comment || bestRating.mentorComment?.S || bestRating.mentorComment || '',
                    hasRating: true,
                    isUnknownUser: false,
                    duplicateCount: userRatings.length
                });
            });
            
            // Set debug information
            setDebugInfo({
                formId: formId,
                usersReceived: validUsers.length,
                ratingsReceived: validRatings.length,
                filteredRatings: formRatings.length,
                finalUsers: submittedUsersData.length,
                sampleRating: formRatings[0] || null,
                sampleUser: validUsers[0] || null,
                timestamp: new Date().toISOString()
            });
            
            setSubmittedUsers(submittedUsersData);
            setRetryCount(0); // Reset retry count on success
            
        } catch (error) {
            console.error('Error fetching submitted users:', error);
            setError(`Failed to load submitted users: ${error.message}`);
            
            // Set debug info even on error
            setDebugInfo({
                error: error.message,
                formId: form.form_id,
                timestamp: new Date().toISOString(),
                retryCount: retryCount
            });
        } finally {
            setIsLoading(false);
        }
    }, [form.form_id, retryCount]);

    const handleRetry = useCallback(() => {
        setRetryCount(prev => prev + 1);
        getRatingFormUsers(true); // Force refresh
    }, [getRatingFormUsers]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Date formatting error:', error);
            return dateString;
        }
    };

    const getInitials = (name) => {
        if (!name || typeof name !== 'string') return '??';
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 0) return '??';
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return parts.map(part => part[0]).join('').substring(0, 2).toUpperCase();
    };

    const handleReviewClick = useCallback((user) => {
        const userWithMentorMode = {
            ...user,
            isMentorMode: true,
            mentorComment: user.mentorComment || ''
        };
        setSelectedUser(userWithMentorMode);
        setOpenPopup(true);
    }, []);

    const handleClosePopup = useCallback(() => {
        setOpenPopup(false);
        setSelectedUser(null);
        // Force refresh when popup closes to get updated data
        getRatingFormUsers(true);
        if (refreshForms) {
            refreshForms();
        }
    }, [refreshForms, getRatingFormUsers]);

    const handleCloseError = useCallback(() => {
        setError(null);
    }, []);

    const handleCloseSuccess = useCallback(() => {
        setSuccessMessage('');
    }, []);

    // Removed handlePopupUpdate - refresh happens on popup close instead

    useEffect(() => {
        if (form?.form_id) {
            getRatingFormUsers();
        }
    }, [getRatingFormUsers, form?.form_id, form]);

    // User Card Component
    const UserCard = ({ user }) => {
        return (
            <Card 
                elevation={0}
                sx={{ 
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        borderColor: alpha('#0a0068', 0.3),
                        boxShadow: `0 4px 12px ${alpha('#0a0068', 0.1)}`,
                    },
                    ...(user.isUnknownUser && {
                        borderColor: alpha(theme.palette.warning.main, 0.5),
                        bgcolor: alpha(theme.palette.warning.main, 0.02)
                    })
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={8} md={9}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar
                                    sx={{
                                        bgcolor: user.isUnknownUser 
                                            ? alpha(theme.palette.warning.main, 0.1)
                                            : alpha('#0a0068', 0.1),
                                        color: user.isUnknownUser 
                                            ? theme.palette.warning.main
                                            : '#0a0068',
                                        width: 48,
                                        height: 48,
                                        fontSize: '1rem',
                                        fontWeight: 600
                                    }}
                                >
                                    {getInitials(user.name)}
                                </Avatar>
                                <Box flex={1}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="h6" color="primary" fontWeight="600">
                                            {user.name}
                                        </Typography>
                                        {user.isUnknownUser && (
                                            <Chip 
                                                label="Unknown User" 
                                                size="small" 
                                                color="warning"
                                                variant="outlined"
                                            />
                                        )}
                                        {user.duplicateCount > 1 && (
                                            <Chip 
                                                label={`${user.duplicateCount} submissions`}
                                                size="small" 
                                                color="info"
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {user.email}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Submitted: {formatDate(user.submittedAt)}
                                        </Typography>
                                    </Stack>
                                    {user.mentorComment && (
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                            <CommentIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                            <Chip 
                                                label="Has mentor comment" 
                                                size="small" 
                                                color="success"
                                                variant="outlined"
                                            />
                                        </Stack>
                                    )}
                                </Box>
                            </Stack>
                        </Grid>
                        
                        <Grid item xs={12} sm={4} md={3}>
                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={() => handleReviewClick(user)}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        fontWeight: 600,
                                        background: 'linear-gradient(135deg, #0a0068 0%, #0c0082 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #0c0082 0%, #0e009e 100%)',
                                        }
                                    }}
                                >
                                    {user.mentorComment ? 'Review Again' : 'Review'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    if (isLoading) {
        return (
            <Box 
                display="flex" 
                flexDirection="column"
                justifyContent="center"
                alignItems="center" 
                minHeight="200px"
            >
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading Users submissions...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Success Message */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Error Message - Only show when there are users to display */}
            {error && submittedUsers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Alert 
                        severity="error" 
                        onClose={handleCloseError}
                        action={
                            <Button 
                                color="inherit" 
                                size="small" 
                                onClick={handleRetry}
                                startIcon={<RefreshIcon />}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                </Box>
            )}

            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" color="primary" fontWeight="600" sx={{ mb: 1 }}>
                            Submissions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {submittedUsers.length === 0 
                                ? 'No users have submitted their self-reflection yet.' 
                                : `${submittedUsers.length} user${submittedUsers.length === 1 ? '' : 's'} submitted their self-reflection.`
                            }
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => getRatingFormUsers(true)}
                        startIcon={<RefreshIcon />}
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        Refresh
                    </Button>
                </Stack>
            </Box>

            {/* Debug Information (Development Only) */}
            {/* {process.env.NODE_ENV === 'development' && debugInfo && (
                <Accordion sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Debug Information
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                            <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem' }}>
                                {JSON.stringify(debugInfo, null, 2)}
                            </Typography>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )} */}

            {/* Users List */}
            {error && submittedUsers.length === 0 ? (
                <Paper 
                    elevation={0}
                    sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        bgcolor: alpha('#f5f5f5', 0.3)
                    }}
                >
                    <PersonIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Failed to Load Submissions
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                        There was an error loading the user submissions. Please try again.
                    </Typography>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleRetry}
                        startIcon={<RefreshIcon />}
                        sx={{ textTransform: 'none' }}
                    >
                        Try Again
                    </Button>
                </Paper>
            ) : submittedUsers.length === 0 ? (
                <Paper 
                    elevation={0}
                    sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        bgcolor: alpha('#f5f5f5', 0.3)
                    }}
                >
                    <PersonIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No Submissions Yet
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                        Users will appear here once they submit their self-reflection.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {submittedUsers.map((user, index) => (
                        <UserCard 
                            key={user.ratingId || user.id || `user-${index}`} 
                            user={user} 
                        />
                    ))}
                </Stack>
            )}
            
            {/* Rating Popup */}
            {selectedUser && (
                <RatingPopup 
                    open={openPopup} 
                    name={selectedUser.name} 
                    userEmail={selectedUser.email} 
                    onClose={handleClosePopup} 
                    questions={questions} 
                    status={status} 
                    form={form}
                    isMentorMode={selectedUser.isMentorMode || false}
                    userRatingData={selectedUser.ratingData}
                    onUpdate={null}
                />
            )}
        </Box>
    );
};

export default RatingFormUsers;